/**
 * SuppressionBanner component
 *
 * Shows on the issue detail page in two modes:
 * - Purple: when the issue itself is SUPPRESSED (with unsuppress action)
 * - Amber: when active suppression rules match the issue (informational)
 */

import { BellOff, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SuppressionRule } from "@/types/suppressionRule";

interface SuppressionBannerProps {
  matchingRules: SuppressionRule[];
  issueStatus?: string;
  onUnsuppress?: () => void;
  isUnsuppressing?: boolean;
}

export function SuppressionBanner({
  matchingRules,
  issueStatus,
  onUnsuppress,
  isUnsuppressing,
}: SuppressionBannerProps) {
  const isSuppressed = issueStatus === "SUPPRESSED";

  if (matchingRules.length === 0 && !isSuppressed) return null;

  const ruleNames = matchingRules.map((r) => r.name);

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors",
        isSuppressed
          ? "border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-200"
          : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
      )}
    >
      <BellOff className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-medium">
          {isSuppressed
            ? "This issue is suppressed"
            : "Future similar issues are being suppressed"}
        </p>
        <p className="text-xs mt-1 opacity-80">
          {matchingRules.length === 1
            ? `Rule "${ruleNames[0]}" matches this issue's criteria.`
            : matchingRules.length > 1
              ? `${matchingRules.length} rules match: ${ruleNames.join(", ")}.`
              : "This issue was suppressed by a rule that may have been modified or deleted."}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {isSuppressed && onUnsuppress && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs border-purple-300 text-purple-700 hover:bg-purple-100 dark:border-purple-700 dark:text-purple-300 dark:hover:bg-purple-900"
            onClick={onUnsuppress}
            disabled={isUnsuppressing}
          >
            {isUnsuppressing ? "Unsuppressing..." : "Unsuppress"}
          </Button>
        )}
        <Link
          to="/issues?tab=suppression-rules"
          className={cn(
            "text-xs font-medium flex items-center gap-1 flex-shrink-0 hover:underline",
            isSuppressed
              ? "text-purple-700 hover:text-purple-900 dark:text-purple-300"
              : "text-amber-700 hover:text-amber-900 dark:text-amber-300"
          )}
        >
          Manage Rules
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
